import { AddressCountryCodeEU, IInvoiceItemUnit } from '@bloomays-lib/types.shared';
import { object, string, number, boolean, array, date, bool } from 'yup';
import { nameRegex, emojiRegex } from './string.utils';

export const internationalPhoneRegex = /^(?:(?:\+|00)?[\s]?(?:\d[\s.-]?){8,15}|\(\d{1,4}\)[\s.-]?(?:\d[\s.-]?){6,12})$/;
export const FR_SIRET_REGEX_STRING = '^\\d{14}$';
export const EU_VAT_REGEX_STRING =
  'ATU[0-9]{8}|BE[01][0-9]{9}|BG[0-9]{9,10}|HR[0-9]{11}|CY[A-Z0-9]{9}|CHE[0-9]{8,10}|CZ[0-9]{8,10}|DK[0-9]{8}|EE[0-9]{9}|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|DE[0-9]{9}|EL[0-9]{9}|HU[0-9]{8}|IE([0-9]{7}[A-Z]{1,2}|[0-9][A-Z][0-9]{5}[A-Z])|IT[0-9]{11}|LV[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|MT[0-9]{8}|NL[0-9]{9}B[0-9]{2}|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SK[0-9]{10}|SI[0-9]{8}|ES(X(-|\\.)?0?\\d{7}(-|\\.)?[A-Z]|[A-Z](-|\\.)?\\d{7}(-|\\.)?[0-9A-Z]|\\d{8}(-|\\.)?[A-Z])|SE[0-9]{12}|GB([0-9]{9}|[0-9]{12}|GD[0-4][0-9]{2}|HA[5-9][0-9]{2})';
export const EU_VAT_SIRET_REGEX_STRING = `${EU_VAT_REGEX_STRING}|${FR_SIRET_REGEX_STRING}`;
export const euVATSiretRegex = new RegExp(EU_VAT_SIRET_REGEX_STRING);
const euVATRegex = new RegExp(EU_VAT_REGEX_STRING);
const yupVATSiret = string().matches(
  euVATSiretRegex,
  'Le siret de la societé doit être un numéro de TVA ou un numéro de SIRET valide',
);
const yupVAT = string().matches(euVATRegex, 'Le siret de la societé doit être un numéro de TVA valide');

const validateStringNoEmoji = (fieldName: string) => {
  return string().test(`${fieldName}: Ne doit contenir que des caractères textuels (pas d'émojis)`, function (value) {
    if (!value) return true; // La validation required est gérée séparément

    if (emojiRegex.test(value)) {
      return this.createError({ message: `${fieldName} ne doit pas contenir d'émojis` });
    }

    return true;
  });
};

const validateStringNoForbiddenCharacters = (fieldName: string) => {
  return string().test(
    `${fieldName}: Ne doit contenir que des caractères textuels (pas de caractères spéciaux)`,
    function (value) {
      if (!value) return true; // La validation required est gérée séparément

      if (!nameRegex.test(value)) {
        return this.createError({ message: `${fieldName} ne doit pas contenir de caractères spéciaux` });
      }

      return true;
    },
  );
};

const schemas: any = {};

schemas.invoice = object({
  invoiceDate: date().required(),
  paymentDate: date().required(),
  title: string().required(),
  draft: boolean().required(),
  currencyISO4217: string().required().is(['EUR']),
  specialMention: string(),
  internalReference: string().required(),
  langage: string().required().oneOf(['fr_FR', 'en_GB']),
  type: string().required().oneOf(['FREE', 'CDI']),
  subtype: string().required('type of mission must be defined (IT, RPO, People').oneOf(['IT', 'RPO', 'People']),
  orderNumber: string().optional(),
  items: array()
    .required()
    .min(1)
    .of(
      object({
        reference: string().required(),
        label: string().required(),
        quantity: number().required(),
        unitAmountWithoutTaxe: number().required(),
        unit: string()
          .required('Seules les unités suivantes sont supportées: ' + Object.values(IInvoiceItemUnit).join(', '))
          .oneOf(Object.values(IInvoiceItemUnit)),
        vatRate: number().required('Only VAT RATE of 0 | 5.5 | 10 | 20 are supported').oneOf([0, 5.5, 10, 20]),
        description: string().required(),
        discountPercent: number().optional(),
      }),
    ),
  customerInfos: object({
    customerName: string().required(),
    siret: string()
      .when('billingCountryCodeAlpha2', (billingCountryCodeAlpha2, schema) => {
        if (!AddressCountryCodeEU?.includes(billingCountryCodeAlpha2)) {
          return schema;
        }
        return yupVATSiret;
      })
      .required(),
    billingAddressName: string().required(),
    billingAddressPart1: string().required(),
    billingAddressPart2: string().optional(),
    billingPostalCode: string().required(),
    billingCity: string().required(),
    billingCountryCodeAlpha2: string()
      .length(2, "Le code pays addressCountryCode de l'adresse de facturation doit contenir deux caractères")
      .required(),
    deliveryAddressName: string().required(),
    deliveryAddressPart1: string().required(),
    deliveryAddressPart2: string().optional(),
    deliveryPostalCode: string().required(),
    deliveryCity: string().required(),
    deliveryCountryCodeAlpha2: string()
      .length(2, "Le code pays addressCountryCode de l'adresse de livraison doit contenir deux caractères")
      .required(),
    recipientName: string().required(),
    vatNumber: string()
      .when('billingCountryCodeAlpha2', (billingCountryCodeAlpha2, schema) => {
        if (!AddressCountryCodeEU?.includes(billingCountryCodeAlpha2)) {
          return schema;
        }
        return yupVAT;
      })
      .optional(),
    paymentConditions: string().required().oneOf(['0', '15', '30', '45', '60', 'custom']),
    billingContacts: array()
      .required()
      .min(1)
      .of(
        object({
          firstname: string().required(),
          lastname: string().required(),
          email: string().email().required(),
          nameJob: string().optional(),
        }),
      ),
    operationalContacts: array()
      .required()
      .min(1)
      .of(
        object({
          firstname: string().required(),
          lastname: string().required(),
          email: string().email().required(),
          nameJob: string().optional(),
        }),
      ),
    phone: string().optional(),
    internalId: string().required(),
  }),
});

schemas.supplierInvoice = object({
  invoiceDate: date().required(),
  paymentDate: date().required(),
  currencyISO4217: string().required().is(['EUR']),
  internalReference: string().required(),
  langage: string().required().oneOf(['fr_FR', 'en_GB']),
  subtype: string().required('type of mission must be defined (IT, RPO, People').oneOf(['IT', 'RPO', 'People']),
  fileB64Encoded: string().required(),
  draft: boolean().required(),
  items: array()
    .required()
    .min(1)
    .of(
      object({
        label: string().required(),
        quantity: number().required(),
        unitAmountWithoutTaxe: number().required(),
        planItemNumber: string().optional(),
        vatRate: number().required('Only VAT RATE of 0 | 5.5 | 10 | 20 are supported').oneOf([0, 5.5, 10, 20]),
      }),
    ),
  supplierInfos: object({
    supplierName: string().required(),
    siret: string()
      .when('billingCountryCodeAlpha2', (billingCountryCodeAlpha2, schema) => {
        if (!AddressCountryCodeEU?.includes(billingCountryCodeAlpha2)) {
          return schema;
        }
        return yupVATSiret;
      })
      .required(),
    billingAddressName: string(),
    billingAddressPart1: string(),
    billingAddressPart2: string(),
    billingPostalCode: string(),
    billingCity: string(),
    billingCountryCodeAlpha2: string()
      .length(2, "Le code pays addressCountryCode de l'adresse de facturation doit contenir deux caractères")
      .required(),
    vatNumber: string()
      .when('billingCountryCodeAlpha2', (billingCountryCodeAlpha2, schema) => {
        if (!AddressCountryCodeEU?.includes(billingCountryCodeAlpha2)) {
          return schema;
        }
        return yupVAT;
      })
      .optional(),
    paymentConditions: string().required().oneOf(['0', '15', '30', '45', '60', 'custom']),
    billingContacts: array()
      .required()
      .min(1)
      .of(
        object({
          firstname: string().required(),
          lastname: string().required(),
          email: string()
            .email('Email mal formé pour le contact de facturation de la societé fournisseur')
            .required('Email obligatoire pour le contact de facturation de la societé fournisseur'),
          nameJob: string().optional(),
        }),
      ),
    phone: string().optional(),
    internalId: string().required(),
  }),
});

schemas.KAM = object({
  firstname: string().required('KAM: Prénom manquant'),
  lastname: string().required('KAM: Nom manquant'),
  email: string().required('KAM: Email manquant').email(`KAM: Format d'email invalide`),
  phoneNumber: string().optional(),
  bloomers: array().optional(),
  billing: array().optional(),
});

schemas.bloomer = object({
  firstname: validateStringNoForbiddenCharacters('Le prénom du Bloomer').required('Bloomer: Prénom manquant'),
  lastname: validateStringNoForbiddenCharacters('Le nom du Bloomer').required('Bloomer: Nom manquant'),
  email: validateStringNoEmoji("L'email du Bloomer")
    .required('Bloomer: Email manquant')
    .email(`Bloomer: Format d'email invalide`),
  accountManagerName: string().required(`Bloomer: il manque le nom de l'Account Manager`),
  phoneNumber: string().required(`Bloomer: il manque le numéro de téléphone du candidat`),
  accountManager: array().of(schemas.KAM.required(`Bloomer: Les données de l'Account Manager sont manquantes`)),
});

schemas.contact = object({
  firstname: string().required('Contact Opérationnel : Prénom manquant'),
  lastname: string().required('Contact Opérationnel : Nom manquant'),
  nameJob: string().required('Contact Opérationnel : Intitulé du poste manquant'),
  email: string()
    .required('Contact Opérationnel : Email manquant')
    .email(`Contact Opérationnel: Format d'email invalide`),
  phoneNumber: string()
    .matches(internationalPhoneRegex, 'Contact Opérationnel: Numéro de téléphone invalide')
    .optional(),
  isBilling: boolean().optional(),
  isOperational: boolean().optional(),
});

schemas.billingContact = object({
  firstname: string().required('Contact de Facturation: Prénom manquant'),
  lastname: string().required('Contact de Facturation: Nom manquant'),
  nameJob: string().required('Contact de Facturation: Intitulé du poste manquant'),
  email: string()
    .required('Contact de Facturation: Email manquant')
    .email(`Contact Facturation: Format d'email invalide`),
  phoneNumber: string()
    .matches(internationalPhoneRegex, 'Contact Facturation: Numéro de téléphone invalide')
    .optional(),
  isBilling: boolean().optional(),
  isOperational: boolean().optional(),
});

schemas.legalContact = object({
  firstname: string().required('Contact Légal: Prénom manquant'),
  lastname: string().required('Contact Légal: Nom manquant'),
  nameJob: string().required('Contact Légal: Intitulé du poste manquant'),
  email: string().required('Contact Légal: Email manquant').email(`Contact Légal: Format d'email invalide`),
  phoneNumber: string().matches(internationalPhoneRegex, 'Contact Légal: Numéro de téléphone invalide').optional(),
  isBilling: boolean().optional(),
  isOperational: boolean().optional(),
});

schemas.society = object({
  name: string().required('Société cliente: Nom manquant'),
  siret: string()
    .when('addressCountryCode', (addressCountryCode, schema) => {
      if (!AddressCountryCodeEU?.includes(addressCountryCode)) {
        return schema;
      }
      return string().matches(
        euVATSiretRegex,
        'Le siret de la societé doit être un numéro de TVA ou un numéro de SIRET valide',
      );
    })
    .required('Société cliente: Siret manquant'),
  tva: string().notRequired().matches(euVATRegex, 'Société cliente: TVA au mauvais format'),
  addressName: string().optional(),
  addressPart1: string().required('Société cliente: adresse manquante'),
  addressZip: string().required('Société cliente: code postal manquant'),
  addressTown: string().required('Société cliente: ville manquante'),
  addressCountryCode: string().length(
    2,
    "Le code pays (Country/Region Code) de l'adresse de la societé doit contenir deux caractères (ex: FR, BE, DE, etc.)",
  ),
  billingContact: array().of(string()),
});

schemas.billingSociety = object({
  name: string().required('Société de facturation: Nom manquant'),
  siret: string()
    .when('addressCountryCode', (addressCountryCode, schema) => {
      if (!AddressCountryCodeEU?.includes(addressCountryCode)) {
        return schema;
      }
      return string().matches(
        euVATSiretRegex,
        'Le siret de la societé de facturation doit être un numéro de TVA ou un numéro de SIRET valide',
      );
    })
    .required('Société de facturation: Siret manquant'),
  tva: string(),
  addressName: string().optional(),
  addressPart1: string().required('Société de facturation: adresse manquante'),
  addressZip: string().required('Société de facturation: code postal manquant'),
  addressTown: string().required('Société de facturation: ville manquante'),
  addressCountryCode: string()
    .length(
      2,
      "Le code pays addressCountryCode de l'adresse de la societé de facturation doit contenir deux caractères",
    )
    .optional(),
});

schemas.pricing = object({
  purchasePricePerDayWithoutTaxes: number().required(
    `Salaire Brut Annuel TTC / TJ d'achat HT en € (Bloomer), Ajoutez le nombre sans espace ni symbole €`,
  ),
  salePricePerDayWithoutTaxes: number().required(
    `Commission CDI HT / TJM de vente HT en € (Client), Ajoutez le nombre sans espace ni symbole €`,
  ),
  astreinteCoef: number().required('Prix: AstreinteCoef manquant'),
  startDate: string().optional(),
  taxes: number().optional(),
  paymentTerms: string().optional(),
  paymentTermsDetails: string().when('paymentTerms', {
    is: 'CUSTOM',
    then: (schema) =>
      schema.required('Les détails des conditions de paiement sont obligatoires pour un paiement personnalisé'),
    otherwise: (schema) => schema.optional(),
  }),
});

schemas.mission = object({
  bloomer: schemas.bloomer.required('Mission: Bloomer manquant'),
  label: string().required('Mission: Nom de mission manquant'),
  pricing: schemas.pricing.required('Mission: Prix manquant'),
  beginMission: string().required('Mission: Début de mission manquant'),
  endMission: string().required('Mission: Fin de mission manquant'),
  clientSociety: schemas.society.required('Mission: Société manquante'),
  contactOperations: schemas.contact.required('Mission: Contact opérationnel manquant'),
  externalId: string().required(),
  type: string().optional(),
  clientPaydateDays: number().nullable().notRequired(),
  bloomerPaydateDays: number().required('Mission: Délai de réglement Bloomer manquant'),
});

schemas.BPASociety = object({
  name: string().required(),
  siret: string().when('addressCountryCode', (addressCountryCode, schema) => {
    if (!AddressCountryCodeEU?.includes(addressCountryCode)) {
      return schema;
    }
    return yupVATSiret;
  }),
  addressTown: string().required('Société cliente: ville manquante'),
});

schemas.BPAricing = object({
  purchasePricePerDayWithoutTaxes: number().required('Prix: Achat manquant'),
  salePricePerDayWithoutTaxes: number().required('Prix: Vente manquante'),
});

schemas.BPABloomer = object({
  fullname: validateStringNoForbiddenCharacters('Le nom complet du Bloomer').required('Bloomer: Nom complet manquant'),
  email: validateStringNoEmoji("L'email du Bloomer")
    .required('Bloomer: Email manquant')
    .email(`Bloomer: Format d'email invalide`),
  accountManagerName: string().required('Bloomer: KAM manquant'),
  accountManager: array().of(schemas.KAM.required('Bloomer: KAM manquant')),
});

schemas.BPA = object({
  bloomer: schemas.BPABloomer.required('Mission: Bloomer manquant'),
  label: string().required('Mission: Nom de mission manquant'),
  pricing: schemas.BPAricing.required('Mission: Prix manquant'),
  beginMission: string().required('Mission: Début de mission manquant'),
  clientSociety: schemas.BPASociety.required('Mission: Société manquante'),
  contactOperations: schemas.contact.required('Mission: Contact manquant'),
  bloomerPaydateDays: number().required('Mission: Délai de réglement Bloomer manquant'),
});

schemas.billing = object({
  description: string().required('Description manquante'),
  type: string().required('Type manquant'),
  billingDate: string().required('Date de démarrage manquante'),
  trialPeriodEndDate: string().required("Date de fin de période d'essaie manquante"),
  customerRecordId: string().required('Société client manquante - Customer'),
  salesPriceWithoutTaxes: number().required(),
  billingContactRecordId: array().of(string()).required('Contact de facturation manquant'),
  accountManagerRecordId: array().of(string().required('Account Manager manquant')),
  paydateDays: number().optional(),
  externalId: string().required(),
  origin: string().required().oneOf(['hiresweet', 'pipefy']),
});

schemas.pipefyCardData = object({
  id: number().required(),
  pipe_id: string().required(),
  title: string().required(),
});

schemas.pipefyColumn = object({
  id: number().required(),
  name: string().required(),
});

schemas.pipefyUser = object({
  id: number().required(),
  email: string().required(`Pipefy User: Email manquant`).email(`Pipefy User: Format d'email invalide`),
  username: string().required(),
});

schemas.moveDealPipefy = object({
  data: object({
    action: string().test('card.move', 'wrong action type', (value) => value === 'card.move'),
    card: schemas.pipefyCardData.required('missing card data'),
    from: schemas.pipefyColumn.required('missing from data'),
    to: schemas.pipefyColumn.required('missing to data'),
    moved_by: schemas.pipefyUser.required('missing moved_by data'),
  }),
});

schemas.recruiters = object({
  buteur: object({
    email: string()
      .required("Informations sur le recruteur non renseignées dans la note 'BPA'")
      .email(`Format d'email du recruteur invalide`),
  }),
  passeur: object({
    email: string()
      .required("Informations sur l'apporteur non renseignées dans la note 'BPA'")
      .email(`Format d'email de l'apporteur invalide`),
  }),
  defenseur: object({
    email: string()
      .required("Informations sur le sourceur non renseignées dans la note 'BPA'")
      .email(`Format d'email du sourceur invalide`),
  }),
  capitaine: object({
    email: string()
      .required("Informations sur le job owner non renseignées dans la note 'BPA'")
      .email(`Format d'email du job owner invalide`),
  }),
});

// TODO rename en WinPayload
schemas.dealPayload = object({
  externalId: string().required(),
  accountManager: array()
    .required()
    .of(
      object({
        email: string().required('Account Manager: Email manquant').email(`Account Manager: Format d'email invalide`),
      }),
    ),
  label: string().required('Le deal doit avoir un libellé'),
  jobTitle: string().required('Titre du poste est manquant. Vérifiez la carte du job.'),
  beginMission: string().required(
    `L'information "Date de début de la mission / du CDI" de la note 'Informations à valider' n'a pas été saisie.`,
  ),
  endMission: string().required(
    `L'information "Date de fin de la mission / de la période d'essai estimée par le client" de la note 'Informations à valider' n'a pas été saisie.`,
  ),
  type: string().required(`Il manque le Type de job. Vérifie la carte du job.`),
  context: string(),
  leadOrigin: string().required(`Il manque l'Origine du Lead. Vérifie la carte du job.`),
  bloomerSource: string().required(`Il manque la source du Bloomer. Vérifie sur la note 'Informations à valider'`),
  externalUrl: string().required(),
  enableCRAUpload: bool().required(
    `La question "Quel sera le système de CRA utilisé ?", de la note 'Informations à valider' n'a pas été repondue.`,
  ),
  bloomerInPortage: bool().required(
    `La question "Le bloomer passe par une boite de portage ?", de la note 'BPA' n'a pas été repondue.`,
  ),
  missionAddress: string().required(`L'information "Adresse de la mission" de la note 'BPA' n'a pas été saisie.`),
  additionnalNotes: string(),

  bloomer: schemas.bloomer
    .shape({ pipefyContactId: string().required(`Il manque l'id carte bloomer sur pipefy`) })
    .required('Deal data: Bloomer manquant'),
  clientSociety: schemas.society.required('Il manque les informations de la Société client.'),
  contactOperations: schemas.contact.required('Il manque les informations du contact opérationnelle chez le client.'),
  clientBillingSociety: schemas.billingSociety.required('Il manque les informations de la Société de Facturation.'),
  pricing: schemas.pricing.required('Il manque les informations du prix.'),
  clientBillingContact: schemas.billingContact.required(
    'Il manque les informations du contact de facturation chez le client.',
  ),
  legalContact: schemas.legalContact,
  recruiters: schemas.recruiters.required(),
  contractType: string().required(`L'information "Forme de contrat" de la note 'BPA' n'a pas été saisie.`),
}).test(
  'cdi-payment-terms',
  'Au moins un mode de facturation est requis pour les CDI. Vérifie sur la note "Informations à valider"',
  function (value) {
    // Vérifie qu'au moins un des deux champs est rempli
    if (value.type?.startsWith('CDI')) {
      return !!value.pricing?.paymentTerms;
    }
    return true;
  },
);

schemas.hiresweetDealPayload = schemas.dealPayload.shape({
  bloomer: schemas.bloomer.shape({
    accountManagerName: string(),
    accountManager: array().of(object({ email: string().required('Account Manager: Email manquant') })),
    hiresweetContactId: string().required('Bloomer: Id contact hiresweet manquant'),
  }),
});

schemas.bloomies = object({
  firstname: string().required('Account Manager: Prénom manquant'),
  lastname: string().required('Account Manager: Nom manquant'),
  email: string().required('Account Manager: Email manquant').email('Account Manager: Email invalide'),
  externalId: string().required('Account Manager: Id externe manquant'),
  role: string().required('Account Manager: Rôle manquant'),
});

const createBloomieSchemaWithPrefix = (prefix: string) => {
  return object({
    firstname: string().required(`${prefix}: Prénom manquant`),
    lastname: string().required(`${prefix}: Nom manquant`),
    email: string().required(`${prefix}: Email manquant`).email(`${prefix}: Email invalide`),
    externalId: string().required(`${prefix}: Id externe manquant`),
    role: string().required(`${prefix}: Rôle manquant`),
  });
};

const recruiterBloomieSchema = createBloomieSchemaWithPrefix('Recruteur');
const jobOwnerBloomieSchema = createBloomieSchemaWithPrefix('Job owner');

schemas.salesDeal = object({
  id: string().required(),
  name: string().required('Nom de la transaction manquant'),
  stage: string().required(),
  society: schemas.society.shape({
    addressCountry: string().required('Société cliente: Pays/Country/Region manquant'),
    addressCountryCode: string()
      .length(
        2,
        "Le code pays (Country/Region Code) de l'adresse de la societé doit contenir deux caractères (ex: FR, BE, DE, etc.)",
      )
      .required('Société client : Le code pays est obligatoire. Corrige sur le ficher hubspot de la société'),
  }),
  recruiter: recruiterBloomieSchema.required(),
  jobOwner: jobOwnerBloomieSchema.required(),
  jobType: string().required('Type de poste manquant'),
  jobTitle: string().required('Titre du poste manquant'),
  dealType: string().required('Deal type manquant. Il est issu du Type de poste.'),
  duration: number().required('Durée de la mission manquante'),
  onSiteDaysPerWeek: number().required('Nombre de jours travaillés manquant'),
  remoteDaysPerWeek: number().required('Nombre de jours en remote manquant'),
  location: string().required('Lieu de la mission manquant'),
  expectedStartDate: string().required('Date de démarrage attendue manquante'),
  activitySector: string().required("Secteur d'activité manquant"),
  context: string().required('Contexte du projet manquant'),
  details: string().required('Détails de la mission manquant'),
  team: string().required("Infos sur l'équipe manquantes"),
  minYearsXP: number().required('Seniorité minimale manquante'),
  scoreCard1: string().required('Critère de scorecard 1 manquant'),
  scoreCard2: string().required('Critère de scorecard 2 manquant'),
  scoreCard3: string().required('Critère de scorecard 3 manquant'),
  scoreCard4: string().required('Critère de scorecard 4 manquant'),
  requiredLanguages: string().required('Langues requises manquantes'),
  confidentialNotes: string().nullable(),
  maxClientBudget: number().required('Budget client max manquant'),
  clientBudgetDetails: string().nullable(),
  maxTalentBudget: number().required('Budget talent max manquant'),
  outOfBudgetProfil: string().nullable(),
  budgetDecisionMaker: string().nullable(),
  isInternalValidation: bool().nullable(),
  clientResearchTrigger: string().required('Déclencheur manquant'),
  interviewProcess: string().required('Déroulement des entretiens manquant'),
  finalValidationResponsable: string().nullable(),
  minTotalProfilesToInclude: string().required('CP coeur ou nb profil min manquant'),
  searchStatus: string().nullable(),
  competitors: string().required('Concurrents manquants'),
  isExclusivity: bool().required('Exclusivité manquante'),
  cvDebriefDate: string().required('Date de debrief CV manquante'),
  isResponsiveClient: bool().nullable(),
  isReliableClient: bool().nullable(),
  isDirectLinkWithDecisionMaker: bool().nullable(),
  priority: string().nullable(),
  isBloomerReplacement: bool().required('Remplacement Bloomer manquant'),
  leadOrigin: string().required('Origine du Lead manquante'),
  isQuickContract: bool().nullable(),
  interlocuteur1Email: string()
    .required(`Il manque l'email du contact marqué comme interlocuteur 1`)
    .email(`Format d'email de l'interlocuteur 1 invalide`),
  interlocuteur2Email: string().email(`Format d'email de l'interlocuteur 2 invalide`).nullable(),
  sendJobQualifEmail: bool().required(
    "La question 'Envoyer mail récap job qualif au client ?' sur Hubspot est obligatoire pour passer un projet en Sourcing started. Remplis l'information",
  ),
});

schemas.quickSalesDeal = schemas.salesDeal.shape({
  duration: number().nullable(),
  remoteDaysPerWeek: number().nullable(),
  expectedStartDate: string().nullable(),
  activitySector: string().nullable(),
  context: string().nullable(),
  details: string().nullable(),
  team: string().nullable(),
  minYearsXP: number().nullable(),
  scoreCard1: string().nullable(),
  scoreCard2: string().nullable(),
  scoreCard3: string().nullable(),
  scoreCard4: string().nullable(),
  requiredLanguages: string().nullable(),
  maxClientBudget: number().nullable(),
  maxTalentBudget: number().nullable(),
  clientResearchTrigger: string().nullable(),
  interviewProcess: string().nullable(),
  minTotalProfilesToInclude: string().nullable(),
  competitors: string().nullable(),
  isExclusivity: bool().nullable(),
  cvDebriefDate: string().nullable(),
  isBloomerReplacement: bool().nullable(),
  sendJobQualifEmail: bool().nullable(),
});

schemas.emailTemplateBaseProject = object({
  jobTitle: string().required('Le Titre du poste est obligatoire'),
});

schemas.emailTemplateBaseCandidat = object({
  lastname: validateStringNoForbiddenCharacters('Le nom du candidat').required(
    'Le nom de famille du candidat est obligatoire',
  ),
  firstname: validateStringNoForbiddenCharacters('Le prénom du candidat').required(
    'Le prénom du candidat est obligatoire',
  ),
  email: validateStringNoEmoji("L'email du candidat")
    .required("L'email du candidat est obligatoire")
    .email("Format d'email du candidat invalide"),
});

schemas.emailTemplateBaseData = object({
  project: schemas.emailTemplateBaseProject.required(),
  candidat: schemas.emailTemplateBaseCandidat.required(),
});

schemas.positionEmailTemplateData = schemas.emailTemplateBaseData.shape({
  project: schemas.emailTemplateBaseProject.shape({
    dealType: string().required('Le type de job est obligatoire. Vérifie sur Hubspot.'),
    location: string().required('Le lieu de la mission est obligatoire. Vérifie sur Hubspot.'),
    duration: string().required(`La durée de la mission/Période d'essai est obligatoire. Vérifie sur Hubspot.`),
    details: string().required('Les détails de la missions sont obligatoires. Vérifie sur Hubspot.'),
    remoteDaysPerWeek: string().required('Le nombre de jours en remote est obligatoire. Vérifie sur Hubspot.'),
    onSiteDaysPerWeek: string().required(
      'Le nombre de jours de travail par semaine est obligatoire. Vérifie sur Hubspot.',
    ),
  }),
  candidat: object({
    firstname: validateStringNoForbiddenCharacters('Le prénom du candidat').required(
      'Le prénom du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    lastname: validateStringNoForbiddenCharacters('Le nom du candidat').required(
      'Le nom de famille du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    email: validateStringNoEmoji("L'email du candidat")
      .required(`L'email du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.`)
      .email("Format d'email du candidat invalide"),
  }),
  clientSocietyName: string().required('Le nom de la société cliente est obligatoire. Vérifie sur Hubspot.'),
  candidateNotes: object({
    possibleStartDate: string().required(
      `La question "Quand peut-il commencer la mission/le job ?" n'a pas de réponse. Vérifie la note Pré Qualifiacation.`,
    ),
    expectedPrice: string().required(
      `La question "Quel est son TJM / Package souhaité ?" n'a pas de réponse. Vérifie la note Pré Qualifiacation.`,
    ),
  }),
});

schemas.BPAClientEmailData = schemas.emailTemplateBaseData.shape({
  project: schemas.emailTemplateBaseProject.shape({
    dealType: string().required('Le type de job est obligatoire. Vérifie sur Hubspot.'),
    onSiteDaysPerWeek: string().required(
      'Le nombre de jours travaillés par semaine est obligatoire. Vérifie sur Hubspot.',
    ),
  }),
  accountManager: object({
    email: string()
      .required('Le champs Dealer est obligatoire. Vérifie sur Hubspot.')
      .email("Format d'email du Dealer invalide"),
  }),
  candidat: object({
    firstname: validateStringNoForbiddenCharacters('Le prénom du candidat').required(
      'Le prénom du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    lastname: validateStringNoForbiddenCharacters('Le nom du candidat').required(
      'Le nom de famille du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    email: validateStringNoEmoji("L'email du candidat")
      .required(`L'email du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.`)
      .email("Format d'email du candidat invalide"),
  }),
  interlocuteurs: object({
    interlocuteur1Email: string()
      .required(
        `L'email de l'interlocuteur n° 1 côté Client est obligatoire. Vérifie sur Hubspot et complète les informations.`,
      )
      .email("Format d'email de l'interlocuteur 1 invalide"),
  }),
  notes: object({
    beginDate: string().required(
      'La date de début de la mission / du CDI est obligatoire. Vérifie la note (Informations à valider)',
    ),
    endDate: string().required(
      `La date de fin de la mission / de la période d'essai estimée par le client. Vérifie la note (Informations à valider)`,
    ),
    salesPrice: number()
      .typeError(
        `La valeur de "Commission CDI HT / TJM de vente HT en €" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        `La valeur de "Commission CDI HT / TJM de vente HT en €" est obligatoire. Vérifie la note (Informations à valider)`,
      ),
    purchasePrice: number()
      .typeError(
        `La valeur de "Salaire Brut Annuel TTC / TJ d'achat HT en €" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        `La valeur de "Salaire Brut Annuel TTC / TJ d'achat HT en €" est obligatoire. Vérifie la note (Informations à valider)`,
      ),
    BDCDuration: number()
      .typeError(
        `La valeur de "Durée du bon de commande (en mois)" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        'La valeur de la "Durée du bon de commande (en mois)" est obligatoire. Vérifie la note (Informations à valider)',
      ),
    clientPaydateDays: number()
      .typeError(
        `La valeur de "Délais de paiement du Client (en jours)" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        `La valeur de "Délais de paiement du Client (en jours)" est obligatoire.. Vérifie la note (Informations à valider)`,
      ),
  }),
});

schemas.BPABloomerEmailData = schemas.emailTemplateBaseData.shape({
  project: schemas.emailTemplateBaseProject.shape({
    dealType: string().required('Le type de job est obligatoire. Vérifie sur Hubspot.'),
    location: string().required('Le lieu de la mission est obligatoire. Vérifie sur Hubspot.'),
    onSiteDaysPerWeek: string().required(
      'Le nombre de jours travaillés par semaine est obligatoire. Vérifie sur Hubspot.',
    ),
  }),
  accountManager: object({
    email: string()
      .required('Le champs Dealer est obligatoire. Vérifie sur Hubspot.')
      .email("Format d'email du Dealer invalide"),
  }),
  candidat: object({
    firstname: validateStringNoForbiddenCharacters('Le prénom du candidat').required(
      'Le prénom du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    lastname: validateStringNoForbiddenCharacters('Le nom du candidat').required(
      'Le nom de famille du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.',
    ),
    email: validateStringNoEmoji("L'email du candidat")
      .required(`L'email du candidat est obligatoire. Vérifie sur Hiresweet et complète les informations.`)
      .email("Format d'email du candidat invalide"),
  }),
  clientSocietyName: string().required('Le nom de la société cliente est obligatoire. Vérifie sur Hubspot.'),
  notes: object({
    beginDate: string().required(
      'La date de début de la mission / du CDI est obligatoire. Vérifie la note (Informations à valider)',
    ),
    endDate: string().required(
      `La date de fin de la mission / de la période d'essai estimée par le client. Vérifie la note (Informations à valider)`,
    ),
    purchasePrice: number()
      .typeError(
        `La valeur de "Salaire Brut Annuel TTC / TJ d'achat HT en €" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        `La valeur de "Salaire Brut Annuel TTC / TJ d'achat HT en €" est obligatoire. Vérifie la note (Informations à valider)`,
      ),
    BDCDuration: number()
      .typeError(
        `La valeur de "Durée du bon de commande (en mois)" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        'La valeur de la "Durée du bon de commande (en mois)" est obligatoire. Vérifie la note (Informations à valider)',
      ),
    bloomerPaydateDays: number()
      .typeError(
        `La valeur de "Délais de paiement du Bloomer (en jours)" ne doit pas contenir autre chose que des chiffres. Vérifie la note (Informations à valider)`,
      )
      .required(
        `La valeur de "Délais de paiement du Bloomer (en jours)" est obligatoire.. Vérifie la note (Informations à valider)`,
      ),
  }),
});

schemas.disqualifiedCandidateEmailTemplateData = schemas.emailTemplateBaseData.shape({
  accountManager: object({
    email: string()
      .required("Informations de l'Account Manager manquant. Vérifie l'owner du projet sur Hiresweet.")
      .email("Format d'email de l' Account Manager invalide"),
  }),
});

export const getSchema = (schemaName: string) => {
  const schema = schemas[schemaName];

  if (schema) return schema;

  throw new Error(`Yup schema not found ${schemaName}`);
};
